import { useCallback, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  Checkbox,
  Button,
  InlineStack,
  Box,
  Icon,
  SkeletonThumbnail,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  EditIcon,
  PlayIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^\s&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};
import { useTranslation } from "react-i18next";
import { AddMediaModal } from "@shared/ui";
import {
  Event,
  GallerySection,
  MultilingualText,
  MediaItem,
  ImageDetailType,
  Instructor,
  Venue
} from "@features/events/types";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { GenerativeTextField } from "@shared/ui";
import { MediaEntityType } from "@shared/ui";
import { getLocalizedText } from "@shared/lib";

interface MediaItemComponentProps {
  item: MediaItem;
  onRemove: (itemId: string) => void;
  onEdit: (item: MediaItem) => void;
}

const MediaItemComponent: React.FC<MediaItemComponentProps> = ({
  item,
  onRemove,
  onEdit,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`Edit ${getLocalizedText(item.name) || "media item"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(item.name) || "media item"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            {item.type === "vid" ? (
              item.source && getYouTubeThumbnail(item.source as string) ? (
                <img
                  src={getYouTubeThumbnail(item.source as string)!}
                  alt={getLocalizedText(item.name)}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Icon source={PlayIcon} />
              )
            ) : item.source ? (
              <img
                src={(item.source as ImageDetailType[])[0]?.src || ""}
                alt={getLocalizedText(item.name)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <SkeletonThumbnail size="small" />
            )}

            <BlockStack gap="100">
              <Text as="span" variant="bodyMd">
                {getLocalizedText(item.name)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(item)}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(item.name.bg)}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

interface EventPhotosVideosCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
  eventInstructors?: Partial<Instructor>[];
  eventVenues?: Partial<Venue>[];
}

export default function EventPhotosVideosCard({
  eventData,
  onDataChange,
  fieldErrors = {},
  eventInstructors,
  eventVenues,
}: EventPhotosVideosCardProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();

  // Extract current values from eventData.gallery
  const gallery = eventData.gallery;
  const enablePhotosVideos = Boolean(gallery);
  const sectionTitle = gallery?.title;
  const sectionDesc = gallery?.description;
  // Convert API format to MediaItem format for display
  const mediaItems: MediaItem[] = useMemo(() => {
    return (
      gallery?.media?.map((media) => ({
        type: media.type === "vid" ? "vid" : "img",
        name: { bg: media.name.bg, en: media.name.en },
        source: media.source,
      })) || []
    );
  }, [gallery]);

  const updateGallery = useCallback(
    (
      updates: Partial<{
        enabled: boolean;
        title: MultilingualText;
        description: MultilingualText;
        media: MediaItem[];
      }>
    ) => {
      if (updates.enabled === false) {
        // Remove gallery when disabled
        onDataChange({ gallery: undefined });
        return;
      }

      // Convert MediaItem format to API format
      const galleryData: GallerySection  = {
        title: updates.title ?? sectionTitle ?? {},
        description: updates.description ?? sectionDesc ?? {},
        media: (updates.media ?? mediaItems).map(
          (item): MediaItem => ({
            type: item.type === "vid" ? "vid" : "img",
            name: { bg: item.name.bg, en: item.name.en },
            source: item.source || "",
          })
        ),
      };

      onDataChange({ gallery: galleryData });
    },
    [onDataChange, sectionTitle, sectionDesc, mediaItems]
  );

  const handleTogglePhotosVideos = (checked: boolean) => {
    updateGallery({ enabled: checked });
  };

  const renderMediaItem = (
    item: MediaItem
  ) => (
    <MediaItemComponent
      item={item}
      onRemove={handleRemoveMedia}
      onEdit={handleEditClick}
    />
  );

  const handleAddMedia = (mediaArray: MediaItem[]) => {
    updateGallery({ media: [...mediaItems, ...mediaArray] });
  };

  const handleEditMedia = (updatedMedia: MediaItem) => {
    const updatedItems = mediaItems.map((item) => 
      item.name.bg === updatedMedia.name.bg ? updatedMedia : item
    );
    updateGallery({ media: updatedItems });
  };

  const handleRemoveMedia = (id: string) => {
    const updatedItems = mediaItems.filter((item) => item.name.bg !== id);
    updateGallery({ media: updatedItems });
  };

  const handleEditClick = (item: MediaItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("eventDetails.photosVideos.title")}
        </Text>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("eventDetails.photosVideos.description")}
        </Text>

        <Checkbox
          label={t("eventDetails.photosVideos.enableCheckbox")}
          checked={enablePhotosVideos}
          onChange={handleTogglePhotosVideos}
        />

        {enablePhotosVideos && (
          <BlockStack gap="400">
            <BlockStack gap="300">
              <GenerativeTextField
                id="section-title-bg"
                placementId="event.event.section.title"
                label={t("eventDetails.photosVideos.sectionTitle")}
                value={sectionTitle ?? {}}
                onChange={(value) => updateGallery({ title: value })}
                type="textField"
                canGenerate={true}
                requiredIndicator
                error={fieldErrors["gallery.title"]}
              />
              <GenerativeTextField
                id="section-description-bg"
                placementId="event.event.section.description"
                label={t("eventDetails.photosVideos.sectionDescription")}
                value={sectionDesc ?? {}}
                onChange={(value) => updateGallery({ description: value })}
                type="textArea"
                canGenerate={true}
              />
            </BlockStack>

            <BlockStack gap="300">
              <Text as="h3" variant="bodyMd">
                {t("eventDetails.photosVideos.mediaGallery")}
              </Text>

              {mediaItems.length === 0 ? (
                <Box padding="600" borderRadius="200">
                  <InlineStack align="center">
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {t("eventDetails.photosVideos.noMediaAdded")}
                    </Text>
                  </InlineStack>
                </Box>
              ) : (
                <Box
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  background="bg-surface"
                >
                  <SortableList
                    items={mediaItems}
                    onChange={(reorderedMedia) => {
                      updateGallery({ media: reorderedMedia });
                    }}
                    idForItem={(item) => `${item?.name?.bg}-${item?.name?.en}`}
                    renderItem={(item) => renderMediaItem(item)}
                    showDividers={true}
                  />
                </Box>
              )}

              <InlineStack align="center">
                <Button
                  icon={PlusIcon}
                  variant="primary"
                  onClick={() => setIsModalOpen(true)}
                >
                  {t("eventDetails.photosVideos.addMedia")}
                </Button>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        )}
      </BlockStack>

      <AddMediaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddMedia={handleAddMedia}
        onEditMedia={handleEditMedia}
        editingItem={editingItem}
        hcc={hcc}
        entityType={MediaEntityType.EVENT}
        entityId={eventData.id}
        eventData={eventData}
        eventInstructors={eventInstructors}
        eventVenues={eventVenues}
      />
    </Card>
  );
}
